/**
 * Password Gate Component
 * 
 * This component provides site-wide password protection.
 * 
 * TO DISABLE PASSWORD PROTECTION:
 * 1. In App.js, remove the <PasswordGate> wrapper around <AppRouter />
 * 2. In index.html, remove the noindex meta tag
 * 
 * Password: huB123 (case-sensitive)
 * 
 * WHITELISTED PATHS (no password required):
 * - /rate/* (public rating pages for homeowners)
 */

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Lock, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

// Site password - change this to update the password
const SITE_PASSWORD = "huB123";
const SESSION_KEY = "site_access_granted";

// Paths that don't require site password
const WHITELISTED_PATHS = [
  /^\/rate\//,  // Public rating pages
];

export default function PasswordGate({ children }) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Check if current path is whitelisted
  const isWhitelisted = WHITELISTED_PATHS.some(pattern => pattern.test(location.pathname));

  // Check if already authenticated on mount
  useEffect(() => {
    const hasAccess = sessionStorage.getItem(SESSION_KEY) === "true";
    setIsAuthenticated(hasAccess);
    setIsLoading(false);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (password === SITE_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "true");
      setIsAuthenticated(true);
    } else {
      setError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  // Show loading state briefly
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If path is whitelisted or authenticated, render children
  if (isWhitelisted || isAuthenticated) {
    return children;
  }

  // Password gate UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/20">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Contractor Hub LLC</h1>
          <p className="text-slate-400 text-sm">This site is password protected</p>
        </div>

        {/* Password Form */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Enter Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter site password"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500"
                autoFocus
                data-testid="password-gate-input"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span data-testid="password-error">{error}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5"
              data-testid="password-gate-submit"
            >
              Enter Site
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Contact the site administrator if you need access.
        </p>
      </div>
    </div>
  );
}
